import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { EditProductFlow } from '../../../components/EditProductFlow';
import { useProductEdit } from '../productsContext';
import type { VerificacionApi } from './useVerificacion';
import type { ProductoDeImagen } from './tipos';
import { configuracionDeImagen } from './api';
import { configuracionDeRegeneracion, sePuedeConfigurar } from './configurarRegeneracion';
import { BarraResultado } from './BarraResultado';
import { BotonVerificar } from './BotonVerificar';
import { ModalExplicacion } from './ModalExplicacion';
import { PopoverResultado } from './PopoverResultado';

/**
 * Pedir el análisis de una imagen con el modal de primera vez adelante
 * (ADR-2026-09-21-verificar-va-en-la-barra-de-la-celda): lo usa la capa para su botón y
 * cualquier otro botón que dispare lo mismo desde otro lado (la barra de la celda del
 * lienzo). Sin `api` (la función apagada) no hace nada.
 */
export function usePedirAnalisis(
  api: VerificacionApi | undefined,
  id: string,
): { pedir: () => void; modal: React.ReactNode } {
  const [explicando, setExplicando] = useState(false);
  const pedir = () => {
    if (!api) return;
    if (api.necesitaExplicacion) {
      setExplicando(true);
      return;
    }
    void api.analizar(id);
  };
  const modal =
    explicando && api ? (
      <ModalExplicacion
        onAnalizar={() => {
          api.marcarExplicada();
          setExplicando(false);
          void api.analizar(id);
        }}
        onCerrar={() => {
          api.marcarExplicada();
          setExplicando(false);
        }}
      />
    ) : null;
  return { pedir, modal };
}

/**
 * Lo que la verificación dibuja sobre UNA card, según su estado: el botón, el velo de
 * "analizando", o la pastilla de resultado (que abre el popover). Va como hermano del
 * botón-card, no adentro: un botón dentro de un botón es HTML inválido.
 *
 * También es la que dibuja la BARRA de acciones de la card (abajo a la derecha), porque
 * su botón comparte fila con `accionExtra` —descargar o enviar a lanzamiento— y los dos
 * tienen que moverse juntos (ADR-2026-09-07-acciones-rapidas-en-la-card). Por eso ya no
 * puede devolver `null` cuando la imagen no es analizable: la acción extra va igual.
 */
export const CapaVerificacion: React.FC<{
  id: string;
  api: VerificacionApi;
  /** La otra acción rápida de la card, a la derecha del botón de verificar. */
  accionExtra?: React.ReactNode;
  /**
   * Avisa que se abrió el editor de producto, para que el contenedor le CEDA el z-index.
   *
   * El sheet que contiene la columna de resultados es `fixed z-[100]`, y `VariantForm`
   * portalea al body con `z-[60]/[61]`: sin esto el editor queda DEBAJO del sheet y lo
   * único que se ve es su velo oscuro sobre la sidebar. Es el mismo problema —y la misma
   * solución (`cederZIndex`)— que el visor del historial
   * (ADR-2026-09-09-el-editor-de-producto-le-pide-el-z-index-a-la-columna).
   */
  onEditorAbierto?: (abierto: boolean) => void;
  /**
   * Sin el botón de verificar sobre la imagen: quien la monta lo pone en otro lado (la
   * barra de la celda del lienzo, ADR-2026-09-21-verificar-va-en-la-barra-de-la-celda)
   * con `usePedirAnalisis`. El velo, la pastilla y el popover siguen acá.
   */
  sinBoton?: boolean;
}> = ({ id, api, accionExtra, onEditorAbierto, sinBoton = false }) => {
  const estado = api.estadoDe(id);
  const [ancla, setAncla] = useState<DOMRect | null>(null);
  const { pedir: pedirAnalisis, modal: modalExplicacion } = usePedirAnalisis(api, id);
  const [ocupado, setOcupado] = useState(false);
  /** El producto cuyo editor está abierto (CTA "Cargar más fotos"). */
  const [editando, setEditando] = useState<ProductoDeImagen | null>(null);
  // Fuera de InterfazV2 (el inline, por ejemplo) el context cae al no-op: el editor abre
  // igual, solo que nadie refresca un catálogo que esa pantalla no muestra.
  const { refreshProducts } = useProductEdit();
  const navigate = useNavigate();
  const [errorConfig, setErrorConfig] = useState<string | null>(null);

  // El aviso va por efecto y no en los handlers: así no hay forma de que el contenedor
  // quede con el z cedido si la card se desmonta con el editor abierto.
  const abierto = editando !== null;
  useEffect(() => {
    onEditorAbierto?.(abierto);
    return () => onEditorAbierto?.(false);
  }, [abierto, onEditorAbierto]);

  const correr = async (fn: () => Promise<void>) => {
    setOcupado(true);
    try {
      await fn();
    } finally {
      setOcupado(false);
      setAncla(null);
    }
  };

  /**
   * "Ajustar y regenerar": pide el pedido guardado, lo traduce a la configuración de la
   * pantalla y navega al modo con eso en el estado de la navegación, que es donde
   * `InterfazV2` lo aplica (ADR-2026-09-09-ajustar-antes-de-regenerar).
   */
  const configurar = async () => {
    setErrorConfig(null);
    try {
      const { pedido, modo_utilizado } = await configuracionDeImagen(id);
      const config = configuracionDeRegeneracion(pedido, modo_utilizado);
      if (!config) {
        setErrorConfig('Esta imagen se generó con una pantalla que no se puede precargar.');
        return;
      }
      setAncla(null);
      navigate(`/interfaz-v2/modo/${config.destino}`, { state: { configRegeneracion: config } });
    } catch (e) {
      setErrorConfig(e instanceof Error && e.message ? e.message : 'No se pudo abrir la configuración.');
    }
  };

  const conResultado = estado.tipo === 'lista' || estado.tipo === 'error';

  return (
    <>
      {/* La barra de acciones: verificar a la izquierda, descargar / enviar a la derecha.
          Se dibuja aunque la imagen no sea analizable (un video, o una sin producto), que
          es cuando el botón de verificar no corresponde pero la otra acción sí. */}
      {((estado.tipo === 'boton' && !sinBoton) || accionExtra) && (
        <div className="absolute bottom-2 right-2 z-20 flex items-center gap-1.5">
          {estado.tipo === 'boton' && !sinBoton && <BotonVerificar onClick={pedirAnalisis} />}
          {accionExtra}
        </div>
      )}

      {estado.tipo === 'analizando' && (
        <span className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 rounded-2xl bg-black/35 text-white backdrop-blur-[2px]">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="text-[12.5px] font-semibold">Analizando…</span>
        </span>
      )}

      {conResultado && (
        <BarraResultado
          analisis={estado.analisis}
          error={estado.tipo === 'error' ? estado.motivo : null}
          onAbrir={(rect) => {
            api.limpiarError();
            setErrorConfig(null);
            setAncla(rect);
          }}
        />
      )}

      {ancla && conResultado && (
        <PopoverResultado
          ancla={ancla}
          analisis={estado.analisis}
          motivoError={estado.tipo === 'error' ? estado.motivo : null}
          regenerable={api.regenerableDe(id)}
          puedeRegenerar={api.puedeRegenerar}
          cantidadMalas={api.malas.length}
          creditos={api.creditosDe(id)}
          creditosMalas={api.creditosDeMalas}
          productos={api.productosDe(id)}
          modalidad={api.modalidadDe(id)}
          puedeConfigurar={api.regenerableDe(id) && sePuedeConfigurar(api.modoDe(id))}
          error={api.error ?? errorConfig}
          ocupado={ocupado}
          loteEnCurso={api.regenerandoLote}
          onRegenerar={() => void correr(() => api.regenerar(id))}
          onRegenerarMalas={() => void correr(() => api.regenerarMalas())}
          onCargarFotos={(prod) => {
            setAncla(null);
            setEditando(prod);
          }}
          // No pasa por `correr`: ese cierra el popover al terminar, y si la configuración
          // falla el mensaje se iría con él antes de que nadie lo lea.
          onConfigurar={() => {
            setOcupado(true);
            void configurar().finally(() => setOcupado(false));
          }}
          onReanalizar={() => {
            setAncla(null);
            void api.analizar(id);
          }}
          onCerrar={() => setAncla(null)}
        />
      )}

      {/* "Cargar más fotos" abre el MISMO editor de producto que Mi marca y los pickers
          (ADR-2026-09-08-cargar-fotos-en-vez-de-regenerar): ahí adentro ya está resuelto
          subir, borrar y elegir la principal. `VariantForm` portalea a body, así que no lo
          recorta el `overflow-hidden` de la card. */}
      {editando && api.companyId && (
        <EditProductFlow
          companyId={api.companyId}
          product={{ id: editando.variante_id, name: editando.nombre }}
          onClose={() => setEditando(null)}
          onSaved={refreshProducts}
        />
      )}

      {modalExplicacion}
    </>
  );
};
